import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../providers/AuthContext';
import { ConfigContext } from '../../providers/ConfigContext';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { SITE_NAME } from '../../config/siteContent';

const DEFAULT_ANNOUNCEMENTS = [
  'Envíos a todo Uruguay',
  'Venta minorista y mayorista'
];

const isLegacyUnconfirmedAnnouncement = (message) => (
  String(message || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase() === 'cambios y devoluciones faciles'
);

const Menu = ({ isAdminAuthenticated }) => {
  const { user, logout } = useContext(AuthContext);
  const { config } = useContext(ConfigContext);
  const cartItems = useSelector(state => state.cart.items);
  const totalItems = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [announcement, setAnnouncement] = useState({
    enabled: true,
    messages: DEFAULT_ANNOUNCEMENTS
  });

  const firstName = user?.displayName?.split(' ')[0] || 'Usuario';
  const announcementMessages = useMemo(
    () => announcement.messages.filter(message => String(message || '').trim()),
    [announcement.messages]
  );
  const tickerMessages = announcementMessages.length > 0
    ? Array.from({ length: 8 }, () => announcementMessages).flat()
    : [];

  useEffect(() => {
    let isMounted = true;

    const loadAnnouncement = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/home-content`);
        const data = await response.json();

        if (!isMounted || !response.ok) {
          return;
        }

        if (data.data?.announcement) {
          const incomingMessages = Array.isArray(data.data.announcement.messages)
            ? data.data.announcement.messages
            : [];
          const hasLegacyUnconfirmedMessage = incomingMessages.some(isLegacyUnconfirmedAnnouncement);

          setAnnouncement({
            enabled: data.data.announcement.enabled !== false,
            messages: hasLegacyUnconfirmedMessage
              ? DEFAULT_ANNOUNCEMENTS
              : incomingMessages.map((message) => (
                String(message || '').trim().toUpperCase() === 'REM URBAN' ? SITE_NAME : message
              ))
          });
        }

      } catch (error) {
        console.error('Error cargando mensajes del header:', error);
      }
    };

    loadAnnouncement();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {announcement.enabled && tickerMessages.length > 0 && (
        <div className="menu-announcement" aria-label="Anuncios de la tienda">
          <div className="menu-announcement-track">
            {tickerMessages.map((message, index) => (
              <span className="menu-announcement-message" key={`${message}-${index}`}>
                {message}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ───── Main Header ───── */}
      <nav
        id="barramenu"
        className="enhanced-header menu-header"
      >
        {/* Left: Hamburger (mobile) + Nav */}
        <div className="menu-nav-area">
          {/* Hamburger for mobile */}
          <button
            className={`modern-hamburger ${isMobileMenuOpen ? 'is-active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Menu"
            style={{ marginRight: '12px' }}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {/* Desktop nav links */}
          <div className="desktop-menu">
            <Link to="/tienda" className="nav-link">Tienda</Link>
            <Link to="/mujer" className="nav-link">Mujer</Link>
            <Link to="/hombre" className="nav-link">Hombre</Link>
            <Link to="/ninos" className="nav-link">Niños</Link>
            <Link to="/ropa-deportiva" className="nav-link">Ropa deportiva</Link>
          </div>
        </div>

        {/* Center: Logo / Brand */}
        <Link to="/" className="brand-link" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {config?.brand?.logo ? (
            <img 
              src={getDisplayImageUrl(config.brand.logo)} 
              alt={SITE_NAME}
              style={{ maxHeight: '40px', maxWidth: '200px', objectFit: 'contain' }} 
            />
          ) : (
            <span className="brand-wordmark">
              {SITE_NAME}
            </span>
          )}
        </Link>

        {/* Right: Icons */}
        <div className="menu-actions">
          <div className="desktop-menu desktop-menu-secondary">
            <Link to="/mayoristas" className="nav-link">Mayoristas</Link>
            <Link to="/nosotros" className="nav-link">Nosotros</Link>
            <Link to="/contacto" className="nav-link">Contacto</Link>
          </div>

          {/* User */}
          {user ? (
            <>
              <Link
                to="/profile"
                className="nav-link nav-icon-link"
                title={firstName}
              >
                <i className="fa-solid fa-user"></i>
              </Link>
              <button
                onClick={() => { logout(); navigate('/user'); }}
                className="nav-icon-button"
                title="Cerrar sesión"
              >
                <i className="fa-solid fa-sign-out-alt"></i>
              </button>
            </>
          ) : (
            <Link to="/user" className="nav-link nav-icon-link">
              <i className="fa-regular fa-user"></i>
            </Link>
          )}

          {/* Cart */}
          <Link
            to="/cart"
            className="nav-link nav-icon-link cart-link"
          >
            <i className="fa-solid fa-bag-shopping"></i>
            {totalItems > 0 && (
              <span className="menu-cart-count">
                {totalItems}
              </span>
            )}
          </Link>

          {/* Admin shortcut */}
          {isAdminAuthenticated && (
            <Link to="/dashboard" className="nav-link nav-icon-link d-none d-md-block">
              <i className="fa-solid fa-chart-line"></i>
            </Link>
          )}
        </div>
      </nav>

      {/* ───── Mobile Sidebar ───── */}
      {isMobileMenuOpen && (
        <div className="mobile-sidebar" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '300px',
          height: '100vh',
          background: '#ffffff',
          zIndex: 1010,
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out',
          padding: '30px 24px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            {config?.brand?.logo ? (
              <img 
                src={getDisplayImageUrl(config.brand.logo)} 
                alt={SITE_NAME}
                style={{ maxHeight: '30px', maxWidth: '150px', objectFit: 'contain' }} 
              />
            ) : (
              <span style={{
                fontFamily: 'var(--font-accent)',
                fontSize: '1.2rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase'
              }}>
                {SITE_NAME}
              </span>
            )}
            <button
              onClick={toggleMobileMenu}
              style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-primary)' }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div className="mobile-nav-links">
            <Link to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Inicio
            </Link>
            <Link to="/tienda" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Tienda
            </Link>

            <Link to="/mujer" className="mobile-nav-link" onClick={toggleMobileMenu}>Mujer</Link>
            <Link to="/hombre" className="mobile-nav-link" onClick={toggleMobileMenu}>Hombre</Link>
            <Link to="/ninos" className="mobile-nav-link" onClick={toggleMobileMenu}>Niños</Link>
            <Link to="/ropa-deportiva" className="mobile-nav-link" onClick={toggleMobileMenu}>Ropa deportiva</Link>
            <Link to="/mayoristas" className="mobile-nav-link" onClick={toggleMobileMenu}>Mayoristas</Link>
            <Link to="/nosotros" className="mobile-nav-link" onClick={toggleMobileMenu}>Nosotros</Link>
            <Link to="/contacto" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Contacto
            </Link>
            <Link to="/cart" className="mobile-nav-link" onClick={toggleMobileMenu}>
              Carrito {totalItems > 0 && `(${totalItems})`}
            </Link>

            <div style={{ borderTop: '1px solid var(--border-light)', marginTop: '16px', paddingTop: '16px' }}>
              {user ? (
                <>
                  <Link to="/profile" className="mobile-nav-link" onClick={toggleMobileMenu}>
                    Mi Cuenta
                  </Link>
                  <button
                    onClick={() => { logout(); navigate('/user'); toggleMobileMenu(); }}
                    className="mobile-nav-link"
                    style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
                  >
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <Link to="/user" className="mobile-nav-link" onClick={toggleMobileMenu}>
                  Iniciar sesión
                </Link>
              )}
              {isAdminAuthenticated && (
                <Link to="/dashboard" className="mobile-nav-link" onClick={toggleMobileMenu}>
                  Dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={toggleMobileMenu}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1009
          }}
        />
      )}
    </>
  );
};

export default Menu;
